import React, { useState, useEffect, useMemo } from 'react';
import { type Interaction } from '@lib/models/Interaction';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import MarkdownExtended from '../../Library/MarkdowmExtended';
// Mobile responsive imports
import { useTheme } from '@mui/material/styles';
import { ModelTraining, WarningAmberRounded } from '@mui/icons-material';
import { useAiChatServices } from '..';
import ReactMarkdown from 'react-markdown';
import MessagePersona, { PERSONA_CONTENT_INDENT } from './MessagePersona';
import MessagePart from './MessagePart';
import { EventType } from '@lib/models/enums';

interface MessageUserProps {
  message: Interaction;
  onLinkClicked?: (link: string, content: string | null) => void;
}

const MessageUser: React.FC<MessageUserProps> = ({ message, onLinkClicked }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [iconAnimation, setIconAnimation] = useState(false);
  const { onRenderPrompt, userMessageRenderer: UserMessage, showPersona } = useAiChatServices()
  // A user turn's rich UI representation (sent via sendMessage(text, { ui })). Rendered in
  // place of the raw question through the same uiTool dispatcher/registry as agent UI tools;
  // re-hydrated from the persisted UserUiContent sibling on reload. Authored by the user, so
  // MessageAgent excludes these and we render them here.
  const userUiParts = message.parts.filter((p) => p.eventType === EventType.UserAnswer);
  const theme = useTheme();

  // Mobile responsive detection
  const lineHeight = 20;

  // Check if there are any parts with thoughtDetails
  let hasThoughtDetails = message.parts.some(part => part.thoughtDetails?.text);
  let hasErrors = message.parts.some(part => part.errorDetails?.errorMessage);

  // Animate icon appearance with delay
  useEffect(() => {
    if (message.turn_complete && hasThoughtDetails) {
      setIconAnimation(true);
      const timer = setTimeout(() => {
        setIconAnimation(false);
      }, 2000); // Increased delay before showing icon

      return () => clearTimeout(timer);
    } else {
      setIconAnimation(false);
    }
  }, [message.turn_complete, hasThoughtDetails]);

  const handleTipsClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleTipsClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const { promptAsString: question } = useMemo(
    () => onRenderPrompt
      ? onRenderPrompt(message.interactionQuestion ?? "")
      : { promptAsString: message.interactionQuestion ?? '' },
    [onRenderPrompt, message.interactionQuestion],
  );

  // Collect all thought details from message parts
  const allThoughtDetails = message.parts
    .filter(part => part.thoughtDetails?.text)
    .map(part => part.thoughtDetails!.text)
    .join('\n\n---\n\n');

  const allErrorParts = message.parts
    .filter(part => part.errorDetails?.errorMessage)
    .map(part => part.errorDetails!.errorMessage)
    .join('\n\n---\n\n');

  hasThoughtDetails = hasThoughtDetails && allThoughtDetails.length > 0;
  hasErrors = hasErrors && allErrorParts.length > 0;

  return (
    <>
      {message.interactionQuestion?.trim() ? <MessagePersona tone='light' isUser message={message} /> : null}
      <Box sx={{ pl: showPersona && message.interactionQuestion?.trim() ? `${PERSONA_CONTENT_INDENT}px` : 0 }}>
      {userUiParts.length > 0
        ? userUiParts.map((part) => (
            <MessagePart
              key={`${message.interactionId}-${part.id}`}
              part={part}
              message={message}
              onGoing={false}
              preview={false}
              onLinkClicked={onLinkClicked ?? (() => { })}
            />
          ))
        : UserMessage
          ? <UserMessage message={message} question={question ?? ''} />
          : (
            <Box
              data-message-role="user"
              sx={{
                position: 'relative',
                width: '100%',
                backgroundColor: theme.palette.primary.softBg,
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 1,
                marginBottom: 1,
              }}
            >
              <Box sx={{ display: 'inline-flex', px: 1, width: '100%', alignItems: 'center', flexWrap: 'nowrap' }}>
                <QuestionAnswerIcon
                  sx={{
                    color: 'text.secondary',
                    fontSize: 16,
                    mt: 0.2,
                    display: 'inline-flex',
                    alignContent: 'flex-start',
                    minWidth: '3%'
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    lineHeight: `${lineHeight}px`,
                    flex: 1,
                    ml: 1,
                    maxHeight: '100px',
                    overflow: 'auto',
                    display: 'inline-flex',
                    minWidth: '90%'
                  }}
                >
                  <ReactMarkdown>
                    {question}
                  </ReactMarkdown>
                </Typography>

                {/* TipsAndUpdates icon with expand and parallax animation */}
                <Collapse
                  in={true}
                  orientation="horizontal"
                  timeout={2400}
                  sx={{
                    transformOrigin: 'right center',
                    '& .MuiCollapse-wrapper': {
                      transform: 'translateX(0)',
                      transition: 'transform 2.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                    display: 'inline-flex', alignItems: 'flex-end',
                    width: '8%'
                  }}
                >
                  {message.turn_complete && (hasErrors) && (
                    <IconButton
                      onClick={handleTipsClick}
                      sx={{
                        color: iconAnimation ? theme.palette.warning.light : theme.palette.warning.dark,
                        ml: 1,
                        transform: iconAnimation ? 'scale(1.2)' : 'scale(1.0)',
                        transition: 'all 2.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          color: theme.palette.primary.dark,
                          transform: 'scale(1.2)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        },
                        p: 0.3,
                        '&:active': {
                          transform: 'scale(1.2)',
                          transition: 'all 0.1s ease-in-out',
                        },
                        display: 'inline-flex', alignItems: 'center', flex: 1
                      }}
                    >
                      <WarningAmberRounded
                        sx={{
                          fontSize: 22,
                          transition: 'all 2.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      />
                    </IconButton>
                  )}

                  {message.turn_complete && (hasThoughtDetails) && (
                    <IconButton
                      onClick={handleTipsClick}
                      sx={{
                        color: iconAnimation ? theme.palette.secondary.main : theme.palette.secondary.main,
                        ml: 1,
                        transform: iconAnimation ? 'scale(1.2)' : 'scale(1.0)',
                        transition: 'all 2.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          color: theme.palette.primary.dark,
                          transform: 'scale(1.2)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        },
                        p: 0.3,
                        '&:active': {
                          transform: 'scale(1.2)',
                          transition: 'all 0.1s ease-in-out',
                        },
                        display: 'inline-flex', alignItems: 'center'
                      }}
                    >
                      <ModelTraining
                        sx={{
                          fontSize: 22,
                          transition: 'all 2.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      />
                    </IconButton>
                  )}
                </Collapse>
              </Box>

              {/* Popover for thought details */}
              {hasThoughtDetails && allThoughtDetails && <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleTipsClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                slotProps={
                  {
                    paper: {
                      style: {
                        borderRadius: '10px',
                        maxWidth: 600,
                        maxHeight: 400,
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        border: `1px solid ${theme.palette.background.default}`,
                      }
                    }
                  }
                }
              >
                <Paper sx={{ p: 2, backgroundColor: 'transparent' }}>
                  <Typography variant="h6" sx={{ color: 'text.primary', mb: 2, fontWeight: 'bold' }}>
                    Reasoning
                  </Typography>
                  <Box sx={{ color: 'text.primary' }}>
                    <MarkdownExtended
                      sessionId=''
                      text={allThoughtDetails}
                      onLinkClicked={onLinkClicked}
                    />
                  </Box>
                </Paper>
              </Popover>}

              {hasErrors && allErrorParts && <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleTipsClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                slotProps={
                  {
                    paper: {
                      style: {
                        borderRadius: '10px',
                        maxWidth: 600,
                        maxHeight: 400,
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        border: `1px solid ${theme.palette.background.default}`
                      }
                    }
                  }
                }
              // PaperProps={{
              //   sx: {
              //     maxWidth: 600,
              //     maxHeight: 400,
              //     overflow: 'auto',
              //     backgroundColor: 'rgba(0, 0, 0, 0.9)',
              //     border: '1px solid rgba(255, 255, 255, 0.2)',

              //   }
              // }}
              >
                <Paper sx={{ p: 2, backgroundColor: 'transparent' }}>
                  <Typography variant="h6" sx={{ color: 'text.primary', mb: 2, fontWeight: 'bold' }}>
                    Server warnings
                  </Typography>
                  <Box sx={{ color: 'text.primary', fontStyle: 'italic' }}>
                    <MarkdownExtended
                      sessionId=''
                      text={allErrorParts}
                      onLinkClicked={onLinkClicked}
                    />
                  </Box>
                </Paper>
              </Popover>}
            </Box>
        )}
      </Box>
    </>
  );
};

export default MessageUser; 